import {
  Box,
  Grid,
  IconButton,
  Link,
  ThemeProvider,
  Typography,
} from "@mui/material";
import YouTubeIcon from "@mui/icons-material/YouTube";
import FacebookIcon from "@mui/icons-material/FacebookSharp";
import InstagramIcon from "@mui/icons-material/Instagram";
import theme, { backgroundColor } from "src/styles/mainstyle";
import { useState } from "react";
import SignupModal from "../modals/SignUpModal";
import { getIsSignedIn } from "src/redux/reducers/user";
import MainButton from "../buttons/mainbutton";
import { useNavigate, useLocation } from "react-router-dom";
const logo = process.env.PUBLIC_URL + "/hslogo_transparent.png";

type FooterProps = {};

export default function Footer({}: FooterProps) {
  const [openSignUpModal, setSignUpModal] = useState(false);
  const isSignedIn = getIsSignedIn();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (e: React.MouseEvent, sectionId: string) => {
    e.preventDefault();

    if (location.pathname === "/") {
      // Same page - smooth scroll
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } else {
      // Different page - navigate with hash
      navigate(`/#${sectionId}`);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          pt: 8,
          pb: 4,
          mt: 12,
        }}
      >
        <Box
          sx={{
            maxWidth: "1200px",
            margin: "0 auto",
            px: { xs: 3, md: 6 },
          }}
        >
          <Grid container spacing={4}>
            {/* Column 1: Contact & Social */}
            <Grid item xs={12} md={5}>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, pb: 2 }}
              >
                <img
                  src={logo}
                  alt="International Home Swap Logo"
                  style={{ height: "2rem" }}
                />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  International Home Swap
                </Typography>
              </Box>

              <Link
                href="mailto:contact@internationalhomeswap.com"
                underline="hover"
                color="inherit"
                sx={{ display: "block", pb: 2 }}
              >
                contact@internationalhomeswap.com
              </Link>

              {/* Social icons */}
              <Box sx={{ display: "flex", gap: 1, pb: 2 }}>
                <IconButton
                  component="a"
                  href="https://www.youtube.com/@InternationalHomeSwap"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: backgroundColor,
                    border: `1px solid ${backgroundColor}`,
                    "&:hover": {
                      backgroundColor: backgroundColor,
                      color: "white",
                    },
                  }}
                >
                  <YouTubeIcon />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://www.facebook.com/groups/602391395868022"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: backgroundColor,
                    border: `1px solid ${backgroundColor}`,
                    "&:hover": {
                      backgroundColor: backgroundColor,
                      color: "white",
                    },
                  }}
                >
                  <FacebookIcon />
                </IconButton>
                <IconButton
                  component="a"
                  href="https://www.instagram.com/internationalhomeswap"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: backgroundColor,
                    border: `1px solid ${backgroundColor}`,
                    "&:hover": {
                      backgroundColor: backgroundColor,
                      color: "white",
                    },
                  }}
                >
                  <InstagramIcon />
                </IconButton>
              </Box>
            </Grid>

            {/* Column 2: About */}
            <Grid item xs={12} sm={6} md={3.5}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  pb: 2,
                  borderBottom: `2px solid ${backgroundColor}`,
                  display: "inline-block",
                  mb: 2,
                }}
              >
                About
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Link
                  href="/#how-it-works"
                  color="inherit"
                  underline="hover"
                  onClick={(e) => handleNavClick(e, "how-it-works")}
                >
                  How it works
                </Link>
                <Link
                  href="/#about-us"
                  color="inherit"
                  underline="hover"
                  onClick={(e) => handleNavClick(e, "about-us")}
                >
                  About us
                </Link>
                <Link href="/faq" color="inherit" underline="hover">
                  FAQ
                </Link>
                <Link
                  href="/terms-and-condition"
                  color="inherit"
                  underline="hover"
                >
                  Terms & Conditions
                </Link>
              </Box>
            </Grid>

            {/* Column 3: Community */}
            <Grid item xs={12} sm={6} md={3.5}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  pb: 2,
                  borderBottom: `2px solid ${backgroundColor}`,
                  display: "inline-block",
                  mb: 2,
                }}
              >
                Community
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Link href="/homes" color="inherit" underline="hover">
                  Find Homes
                </Link>
                <Link href="/articles" color="inherit" underline="hover">
                  Blog & Articles
                </Link>
                {!isSignedIn && (
                  <Box sx={{ pt: 1 }}>
                    <MainButton
                      text="Sign up!"
                      onClick={() => setSignUpModal(true)}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Bottom bar */}
          <Box
            sx={{
              borderTop: "1px solid #ddd",
              mt: 6,
              pt: 3,
              textAlign: "center",
            }}
          >
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} International Home Swap. All rights
              reserved.
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: "block",
                pt: 1,
                color: "text.secondary",
                fontStyle: "italic",
              }}
            >
              Found a bug or have ideas? Let us know!
            </Typography>
          </Box>
        </Box>
      </Box>

      <SignupModal
        open={openSignUpModal}
        onClose={() => setSignUpModal(false)}
      />
    </ThemeProvider>
  );
}
